import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["field", "submitButton"]

  initialState = {}

  connect() {
    this.captureInitialState()
    this.updateSubmitButton()
    this.addEventListeners()
  }

  disconnect() {
    this.removeEventListeners()
  }

  fieldTargetConnected(field) {
    if (!this.initialState) return

    this.attachFieldListener(field)
    this.updateSubmitButton()
  }

  fieldTargetDisconnected(field) {
    this.detachFieldListener(field)
    this.updateSubmitButton()
  }

  captureInitialState() {
    this.initialState = this.getCurrentState()
  }

  getCurrentState() {
    const currentState = {}

    this.fieldTargets.forEach(field => {
      const key = this.getFieldKey(field)
      const value = this.getFieldValue(field)

      if (key in currentState) {
        if (!Array.isArray(currentState[key])) currentState[key] = [ currentState[key] ]
        currentState[key].push(value)
      } else {
        currentState[key] = value
      }
    })

    return currentState
  }

  getFieldKey(element) {
    return element.name || element.id
  }

  getFieldValue(element) {
    if (element.tomselect) {
      const values = element.tomselect.getValue()
      if (Array.isArray(values)) {
        return values.toSorted()
      }
      return values
    }

    if (element.tagName === 'SELECT') {
      if (element.multiple) {
        return Array.from(element.selectedOptions).map(opt => opt.value).toSorted()
      }
      return element.value
    }

    if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
      return element.value.trim()
    }

    return element.value
  }

  hasChanges() {
    const currentState = this.getCurrentState()
    const allKeys = new Set([...Object.keys(this.initialState), ...Object.keys(currentState)])

    for (const key of allKeys) {
      const initial = this.initialState[key]
      const current = currentState[key]

      const isArrayInitial = Array.isArray(initial)
      const isArrayCurrent = Array.isArray(current)

      if (isArrayInitial || isArrayCurrent) {
        if (!isArrayInitial || !isArrayCurrent) return true
        if (initial.length !== current.length) return true
        if (!initial.every((val, i) => val === current[i])) return true
      } else if (initial !== current) {
        return true
      }
    }

    return false
  }

  addEventListeners() {
    this.fieldTargets.forEach(field => this.attachFieldListener(field))
  }

  removeEventListeners() {
    this.fieldTargets.forEach(field => this.detachFieldListener(field))
  }

  attachFieldListener(field) {
    if (field.tagName === 'INPUT' || field.tagName === 'TEXTAREA') {
      field.addEventListener('input', this.handleFieldChange)
    }

    if (field.tagName === 'SELECT') {
      field.addEventListener('change', this.handleFieldChange)
    }

    if (field.tomselect) {
      field.tomselect.on('change', this.handleTomSelectChange)
    }
  }

  detachFieldListener(field) {
    field.removeEventListener('input', this.handleFieldChange)
    field.removeEventListener('change', this.handleFieldChange)

    if (field.tomselect) {
      field.tomselect.off('change', this.handleTomSelectChange)
    }
  }

  handleTomSelectChange = () => {
    this.updateSubmitButton()
  }

  handleFieldChange = (event) => {
    const element = event.target

    if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
      clearTimeout(this.inputTimeout)
      this.inputTimeout = setTimeout(() => {
        this.updateSubmitButton()
      }, 300)
    } else {
      this.updateSubmitButton()
    }
  }

  updateSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !this.hasChanges()
    }
  }
}
